/**
 * Activities Grid — AG Grid with tree data for collapsible WBS hierarchy.
 * Macro columns: WBS/Name, Dur, Start, Finish, BL Start, BL Finish, Act Start,
 * Act Finish, Predecessors, Constraint, Weightage, Progress, Float, CP, Status, Obj.
 * Each macro row can be expanded into the discipline schedules that deliver it.
 */
import { useRef, useMemo, useCallback, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Upload, RefreshCw, Search, Download, FileSpreadsheet, Loader2, Layers, AlertTriangle,
  Network, Flame, CalendarClock, X
} from 'lucide-react';
import {
  buildGridRows, filterToDate, aggregateRollup, withAncestors, DISCIPLINE_META, HEALTH_COLORS,
} from './microLinkRows';

const STATUS_COLORS = {
  // the 10 macro-schedule statuses
  COMPLETED_ON_TIME: '#10B981', COMPLETED_LATE: '#F59E0B', ON_TRACK: '#3B82F6',
  AT_RISK: '#EAB308', DELAYED: '#FB923C', OVERDUE: '#EF4444',
  LATE_START: '#F97316', FORECAST_DELAY: '#FBBF24',
  NOT_STARTED_OVERDUE: '#DC2626', NOT_STARTED: '#94A3B8',
  // discipline (micro) health bands
  COMPLETED: '#10B981', COMPLETE: '#10B981', AHEAD: '#22C55E',
  BEHIND: '#F59E0B', CRITICAL: '#EF4444', IN_PROGRESS: '#3B82F6',
};

const STATUS_LABELS = {
  COMPLETED_ON_TIME: 'Completed – On Time', COMPLETED_LATE: 'Completed – Late',
  ON_TRACK: 'On Track', AT_RISK: 'At Risk', DELAYED: 'Delayed',
  OVERDUE: 'Overdue', LATE_START: 'Late Start', FORECAST_DELAY: 'Forecast Delay',
  NOT_STARTED_OVERDUE: 'Not Started – Overdue', NOT_STARTED: 'Not Started',
  COMPLETED: 'Complete', COMPLETE: 'Complete', AHEAD: 'Ahead',
  BEHIND: 'Behind', CRITICAL: 'Critical', IN_PROGRESS: 'In Progress',
};

const STATUS_SHORT = {
  COMPLETED_ON_TIME: 'On time', COMPLETED_LATE: 'Comp. late', ON_TRACK: 'On track',
  AT_RISK: 'At risk', DELAYED: 'Delayed', OVERDUE: 'Overdue',
  LATE_START: 'Late start', FORECAST_DELAY: 'Fc delay',
  NOT_STARTED_OVERDUE: 'NS overdue', NOT_STARTED: 'Not started',
};

// The order the KPI chips and legend read in — worst news first.
const STATUS_ORDER = [
  'OVERDUE', 'NOT_STARTED_OVERDUE', 'DELAYED', 'LATE_START', 'AT_RISK',
  'FORECAST_DELAY', 'ON_TRACK', 'COMPLETED_LATE', 'COMPLETED_ON_TIME', 'NOT_STARTED',
];

const DISCIPLINE_ORDER = ['ENGINEERING', 'PROCUREMENT', 'FABRICATION_CONSTRUCTION',
                          'PRECOMM_COMMISSIONING'];

function ProgressCell({ value, data }) {
  const val = value || 0;
  return (
    <div className="flex items-center gap-1">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${val}%`, backgroundColor: STATUS_COLORS[data?.scheduleStatus] || '#3B82F6' }} />
      </div>
      <span className="text-[10px] w-8 text-right">{Number(val).toFixed(0)}%</span>
    </div>
  );
}

function StatusCell({ value }) {
  const color = STATUS_COLORS[value] || '#94A3B8';
  const label = STATUS_LABELS[value] || (value || '').replace(/_/g, ' ');
  const isBad = ['OVERDUE', 'NOT_STARTED_OVERDUE', 'DELAYED', 'CRITICAL'].includes(value);
  return (
    <Badge
      variant={isBad ? 'destructive' : 'outline'}
      className="text-[10px] h-5 px-1.5"
      style={isBad ? {} : { borderColor: color, color }}
      data-testid={`status-badge-${value}`}
    >
      {label}
    </Badge>
  );
}

function CriticalCell({ value }) {
  return value ? <AlertTriangle className="w-3 h-3 text-red-500" /> : null;
}

function DisciplineCell({ data }) {
  if (data?._kind !== 'MICRO') return null;
  const meta = DISCIPLINE_META[data.discipline];
  if (!meta) return null;
  return (
    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded"
          style={{ background: `${meta.color}1f`, color: meta.color }}
          title={`${meta.label}${data.disciplineName ? ` · ${data.disciplineName}` : ''}`}>
      {meta.short}
    </span>
  );
}

/** The discipline rollup for a macro activity — E / P / F&C / PC, like the macro grid. */
function RollupCell({ data, rollup, onDrill }) {
  if (data?._kind !== 'MACRO') return null;  const r = rollup?.[data.activityId];
  if (!r) return <span className="text-[10px] text-muted-foreground">—</span>;
  return (
    <div className="flex items-center gap-1">
      {DISCIPLINE_ORDER.filter(k => r[k]).map(k => {
        const meta = DISCIPLINE_META[k];
        const cell = r[k];
        return (
          <button key={k}
                className="text-[9px] font-semibold px-1 rounded leading-4 hover:brightness-125"
                onClick={(e) => { e.stopPropagation(); onDrill?.(k); }}
                style={{ background: `${HEALTH_COLORS[cell.health]}22`,
                         color: HEALTH_COLORS[cell.health],
                         border: `1px solid ${meta.color}66` }}
                data-testid={`rollup-chip-${k.toLowerCase()}`}
                title={`${meta.label} — ${cell.count} activities, ${cell.pct}% complete (${cell.health.replace(/_/g, ' ').toLowerCase()}). Click to show just the slipping ones.`}>
            {meta.short} {Math.round(cell.pct)}
          </button>
        );
      })}
    </div>
  );
}

function GroupInnerRenderer({ data, value }) {
  if (!data) return value || '';
  if (data._kind === 'MICRO') {
    const meta = DISCIPLINE_META[data.discipline] || {};
    return (
      <div className="flex items-center gap-1.5 truncate">
        <span className="w-1.5 h-1.5 rounded-sm shrink-0" style={{ background: meta.color }} />
        <span className="font-mono text-[10px] shrink-0" style={{ color: meta.color }}>{data.activityCode}</span>
        <span className="truncate text-[11px] text-muted-foreground">{data.activityName}</span>
      </div>
    );
  }
  const isSummary = data.isSummary || data.activityType === 'SUMMARY';
  return (
    <div className="flex items-center gap-1.5 truncate">
      {isSummary
        ? <Layers className="w-3 h-3 text-amber-500 shrink-0" />
        : <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: STATUS_COLORS[data.scheduleStatus] || '#6B7280' }} />
      }
      <span className="font-mono text-[10px] text-muted-foreground shrink-0">{data.activityCode}</span>
      <span className={`truncate text-xs ${isSummary ? 'font-semibold' : ''}`}>{data.activityName}</span>
    </div>
  );
}

export function ActivitiesGrid({
  mappings, gridSearchText, onGridSearchChange, onRowClicked,
  onSyncSchedule, syncing,
  onMappingTemplate, onMappingExport, onMappingImport, importingMapping,
  onCriticalFilterChange,
  microLinks = [], microLinkCounts = {}, rollup = {}, loadingMicroLinks = false,
  onMicroRowClick, onHeatmapChange, onStatusFilterChange,
  timelineDate = null, timelinePlaying = false, focusTick = 0,
}) {
  const gridRef = useRef(null);
  const mappingFileRef = useRef(null);
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [showMicro, setShowMicro] = useState(true);
  const [heatmap, setHeatmap] = useState(false);
  const [disciplines, setDisciplines] = useState(new Set());
  const [followTimeline, setFollowTimeline] = useState(false);
  const [slippingOnly, setSlippingOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState(() => new Set());

  // KPI counts straight off the synced macro schedule — activities and WBS rows.
  const statusCounts = useMemo(() => {
    const acc = {};
    for (const m of mappings || []) {
      const key = m.scheduleStatus || 'NOT_STARTED';
      const e = acc[key] || (acc[key] = { total: 0, tasks: 0, wbs: 0, objects: 0 });
      e.total += 1;
      e.objects += (m.modelObjects || []).length;
      if (m.isSummary || m.activityType === 'SUMMARY') e.wbs += 1; else e.tasks += 1;
    }
    return acc;
  }, [mappings]);

  const statusKeys = useMemo(() => {
    const present = Object.keys(statusCounts);
    const ordered = STATUS_ORDER.filter(k => present.includes(k));
    return [...ordered, ...present.filter(k => !STATUS_ORDER.includes(k))];
  }, [statusCounts]);

  const toggleStatus = useCallback((key) => setStatusFilter(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  }), []);

  // Picking a status also paints and isolates those activities' 3D objects.
  const firstStatusRun = useRef(true);
  useEffect(() => {
    if (firstStatusRun.current) { firstStatusRun.current = false; return; }
    onStatusFilterChange?.([...statusFilter]);
  }, [statusFilter, onStatusFilterChange]);

  // Clicking a rollup chip drills into that discipline's slipping activities.
  const drillTo = useCallback((discipline) => {
    setShowMicro(true);
    setSlippingOnly(true);
    setDisciplines(new Set([discipline]));
  }, []);

  const clearDrill = useCallback(() => {
    setSlippingOnly(false);
    setDisciplines(new Set());
    gridRef.current?.api?.collapseAll?.();
  }, []);

  // Playing the timeliner, or jumping to Today, narrows the table to the
  // activities that are actually live at that moment.
  useEffect(() => { if (timelinePlaying) setFollowTimeline(true); }, [timelinePlaying]);
  useEffect(() => { if (focusTick > 0) setFollowTimeline(true); }, [focusTick]);

  const rows = useMemo(
    () => buildGridRows({ mappings, microLinks, showMicro, disciplines }),
    [mappings, microLinks, showMicro, disciplines]);

  // WBS rows inherit the discipline split of the tasks beneath them.
  const fullRollup = useMemo(
    () => aggregateRollup(mappings, rollup), [mappings, rollup]);

  const dateFiltered = useMemo(() => {
    if (!followTimeline || !timelineDate) return rows;
    const kept = filterToDate(rows, timelineDate);
    // Never strand the user on an empty table — fall back with the toggle still on.
    return kept.some(r => r._kind === 'MACRO' && !r.isSummary) ? kept : rows;
  }, [rows, followTimeline, timelineDate]);

  const filteredMappings = useMemo(() => {
    let out = dateFiltered;
    if (statusFilter.size) {
      const kept = out.filter(r => r._kind === 'MACRO'
        && statusFilter.has(r.scheduleStatus || 'NOT_STARTED'));
      out = withAncestors(out, kept);
    }
    if (slippingOnly) {
      const kept = out.filter(r => r._kind === 'MICRO'
        && (r._health === 'BEHIND' || r._health === 'CRITICAL'));
      out = withAncestors(out, kept);
    }
    if (!criticalOnly) return out;
    return out.filter(m => m._kind === 'MICRO' || m.isCriticalPath || m.isSummary);
  }, [dateFiltered, criticalOnly, slippingOnly, statusFilter]);

  const cpCount = useMemo(    () => (mappings || []).filter(m => m.isCriticalPath && !m.isSummary).length, [mappings]);
  const microShown = useMemo(
    () => filteredMappings.filter(r => r._kind === 'MICRO').length, [filteredMappings]);

  // A drill is useless if the rows stay folded away, so open the tree onto them.
  useEffect(() => {
    if (!slippingOnly && !statusFilter.size) return;
    const t = setTimeout(() => gridRef.current?.api?.expandAll?.(), 60);
    return () => clearTimeout(t);
  }, [slippingOnly, statusFilter, disciplines, filteredMappings]);

  const getDataPath = useCallback((data) => data._path || [data.activityId], []);

  const autoGroupColumnDef = useMemo(() => ({
    headerName: 'WBS / Task Name',
    minWidth: 300,
    flex: 2,
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: GroupInnerRenderer,
    },
  }), []);

  const fmtDate = (p) => p.value ? new Date(p.value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '\u2014';

  // The heat-map paints macro and discipline rows off the same health bands.
  const heatStyle = useCallback((p) => {
    if (!heatmap || !p.data?._health) return {};
    const c = HEALTH_COLORS[p.data._health];
    if (!c) return {};
    return p.data._kind === 'MICRO'
      ? { backgroundColor: `${c}3d`, borderLeft: `4px solid ${c}` }
      : { backgroundColor: `${c}2e`, borderLeft: `3px solid ${c}` };
  }, [heatmap]);

  const columnDefs = useMemo(() => [
    { headerName: 'Disc', field: 'discipline', width: 70, cellRenderer: DisciplineCell,
      cellClass: 'text-center' },
    { headerName: 'Rollup', field: '_rollup', width: 235, sortable: false,
      cellRenderer: (p) => <RollupCell data={p.data} rollup={fullRollup} onDrill={drillTo} />,
      headerTooltip: 'Progress of this macro activity split by discipline' },
    { headerName: 'Dur', field: 'durationDays', width: 60,
      valueGetter: (p) => {
        const d = p.data?.durationDays;
        if (d == null || d === 0) return '\u2014';
        return `${Math.round(d)}d`;
      },
      cellClass: 'text-xs text-center' },
    { headerName: 'Start', field: 'plannedStart', width: 85, valueFormatter: fmtDate, cellClass: 'text-xs' },
    { headerName: 'Finish', field: 'plannedFinish', width: 85, valueFormatter: fmtDate, cellClass: 'text-xs' },
    { headerName: 'BL Start', field: 'baselineStart', width: 85, valueFormatter: fmtDate, cellClass: 'text-xs' },
    { headerName: 'BL Finish', field: 'baselineFinish', width: 85, valueFormatter: fmtDate, cellClass: 'text-xs' },
    { headerName: 'Act Start', field: 'actualStart', width: 85, valueFormatter: fmtDate, cellClass: 'text-xs' },
    { headerName: 'Act Finish', field: 'actualFinish', width: 85, valueFormatter: fmtDate, cellClass: 'text-xs' },
    { headerName: 'Pred', field: 'predecessors', width: 65, cellClass: 'text-xs text-center',
      cellStyle: (p) => p.value ? { color: '#6366F1', fontWeight: 500 } : {} },
    { headerName: 'Const.', field: 'constraintType', width: 60, cellClass: 'text-[10px] text-center' },
    { headerName: 'Wt%', field: 'weightage', width: 55,
      valueGetter: (p) => (p.data?._kind === 'MICRO' ? p.data.weight : p.data?.weightage),
      valueFormatter: (p) => p.value != null && p.value > 0 ? `${Number(p.value).toFixed(1)}` : '\u2014',
      cellClass: 'text-xs text-center' },
    { headerName: 'Prog%', field: 'physicalProgressPct', width: 90, cellRenderer: ProgressCell },
    { headerName: 'Var(d)', field: 'varianceDays', width: 62, cellClass: 'text-xs text-center',
      valueFormatter: (p) => (p.value == null ? '\u2014' : `+${p.value}`),
      cellStyle: (p) => (p.value ? { color: '#EF4444', fontWeight: 500 } : {}) },
    { headerName: 'Float', field: 'totalFloat', width: 50, cellClass: 'text-xs text-center',
      cellStyle: (p) => p.value != null && p.value <= 0 ? { color: '#EF4444' } : {} },
    { headerName: 'CP', field: 'isCriticalPath', width: 35, cellRenderer: CriticalCell },
    { headerName: 'Status', field: 'scheduleStatus', width: 95, cellRenderer: StatusCell },
    { headerName: 'Obj', field: 'modelObjects', width: 40,
      valueGetter: (p) => p.data?.modelObjects?.length || 0, cellClass: 'text-xs text-center' },
  ], [fullRollup]);

  const defaultColDef = useMemo(() => ({ resizable: true, sortable: true, suppressMovable: true }), []);

  const toggleDiscipline = (key) => setDisciplines(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-1 px-2 py-1 border-b shrink-0 flex-wrap">
        <Search className="w-3 h-3 text-muted-foreground" />
        <Input
          placeholder="Search..."
          value={gridSearchText}
          onChange={e => onGridSearchChange(e.target.value)}
          className="h-6 text-xs border-0 shadow-none focus-visible:ring-0 w-32"
          data-testid="activity-search"
        />
        <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={onSyncSchedule} disabled={syncing} data-testid="sync-schedule-btn">
          <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />Sync
        </Button>
        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5" onClick={onMappingTemplate} data-testid="mapping-template-btn">
          <FileSpreadsheet className="w-3 h-3" />
        </Button>
        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5" onClick={onMappingExport} data-testid="mapping-export-btn">
          <Download className="w-3 h-3" />
        </Button>
        <input type="file" accept=".xlsx,.xls" className="hidden" ref={mappingFileRef}
          onChange={e => { onMappingImport(e.target.files?.[0]); e.target.value = ''; }} />
        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5"
          onClick={() => mappingFileRef.current?.click()} disabled={importingMapping} data-testid="mapping-import-btn">
          {importingMapping ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
        </Button>

        <span className="w-px h-4 bg-border mx-1" />

        <Button
          variant={showMicro ? 'default' : 'ghost'}
          size="sm"
          className="h-6 text-[10px] gap-1"
          onClick={() => setShowMicro(v => !v)}
          data-testid="activities-show-disciplines-toggle"
          title="Show the engineering, procurement, fabrication & construction and pre-commissioning activities linked to each macro activity."
        >
          {loadingMicroLinks ? <Loader2 className="w-3 h-3 animate-spin" /> : <Network className="w-3 h-3" />}
          Discipline links
          <span className="ml-0.5 px-1 rounded font-semibold bg-black/10"
                title={`${microShown} shown of ${microLinks.length} linked`}>
            {microLinks.length}
          </span>
        </Button>

        {showMicro && DISCIPLINE_ORDER.map(key => {
          const meta = DISCIPLINE_META[key];
          const on = disciplines.has(key);
          return (
            <button
              key={key}
              onClick={() => toggleDiscipline(key)}
              className="h-6 px-1.5 rounded text-[9px] font-semibold border transition-colors"
              style={{
                borderColor: meta.color,
                color: on ? '#fff' : meta.color,
                background: on ? meta.color : 'transparent',
              }}
              title={`${meta.label} — ${microLinkCounts[key] || 0} linked activities`}
              data-testid={`activities-discipline-${key.toLowerCase()}`}
            >
              {meta.short} {microLinkCounts[key] || 0}
            </button>
          );
        })}

        {slippingOnly && (
          <Button variant="destructive" size="sm" className="h-6 text-[10px] gap-1"
                  onClick={clearDrill} data-testid="activities-clear-drill">
            <X className="w-3 h-3" />
            {microShown > 0 ? `Slipping only · ${microShown}` : 'Nothing slipping here'}
          </Button>
        )}

        <Button
          variant={heatmap ? 'default' : 'ghost'}
          size="sm"
          className={`h-6 text-[10px] gap-1 ${heatmap ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
          onClick={() => { const next = !heatmap; setHeatmap(next); onHeatmapChange?.(next); }}
          data-testid="activities-kpi-heatmap-toggle"
          title="Paint every macro and discipline row by KPI health — complete, ahead, on track, behind, critical."
        >
          <Flame className="w-3 h-3" />KPI heatmap
        </Button>

        <Button
          variant={followTimeline ? 'default' : 'ghost'}
          size="sm"
          className={`h-6 text-[10px] gap-1 ${followTimeline ? 'bg-sky-600 hover:bg-sky-700' : ''}`}
          onClick={() => setFollowTimeline(v => !v)}
          data-testid="activities-follow-timeliner-toggle"
          title="Only show activities that are live at the timeliner's current date."
        >
          <CalendarClock className="w-3 h-3" />
          {followTimeline && timelineDate ? timelineDate : 'Follow timeliner'}
        </Button>

        <Button
          variant={criticalOnly ? 'destructive' : 'ghost'}
          size="sm"
          className="h-6 text-[10px] gap-1 ml-auto"
          onClick={() => {
            const next = !criticalOnly;
            setCriticalOnly(next);
            onCriticalFilterChange?.(next);
          }}
          data-testid="activities-critical-only-toggle"
          title="Filter to activities on the critical path — also isolates their 3D objects in the viewer."
        >
          <AlertTriangle className="w-3 h-3" />
          Only Critical Path
          {cpCount > 0 && (
            <span className="ml-1 px-1 rounded font-semibold" style={{ background: criticalOnly ? 'rgba(255,255,255,0.25)' : 'rgba(239,68,68,0.15)', color: criticalOnly ? '#fff' : '#EF4444' }}>
              {cpCount}
            </span>
          )}
        </Button>
      </div>

      {statusKeys.length > 0 && (
        <div className="flex items-center gap-1.5 px-2 py-1 border-b text-[9px] shrink-0 flex-wrap"
             data-testid="activities-status-kpis">
          <span className="text-[9px] uppercase tracking-wide text-muted-foreground mr-1">
            Status KPIs
          </span>
          {statusKeys.map(key => {
            const c = statusCounts[key];
            const on = statusFilter.has(key);
            const color = STATUS_COLORS[key] || '#94A3B8';
            return (
              <button
                key={key}
                onClick={() => toggleStatus(key)}
                className="flex items-center gap-1 h-5 px-1.5 rounded border transition-colors"
                style={{
                  borderColor: `${color}80`,
                  background: on ? `${color}2e` : 'transparent',
                  color: on ? color : undefined,
                }}
                title={`${STATUS_LABELS[key] || key} — ${c.tasks} activities, ${c.wbs} WBS rows, ${c.objects} linked 3D objects. Click to filter the grid and paint these objects in 3D.`}
                data-testid={`activities-status-kpi-${key.toLowerCase()}`}
              >
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: color }} />
                <span className={on ? 'font-semibold' : 'text-muted-foreground'}>
                  {STATUS_SHORT[key] || STATUS_LABELS[key] || key}
                </span>
                <span className="font-semibold" style={{ color }}>{c.total}</span>
              </button>
            );
          })}
          {statusFilter.size > 0 && (
            <button onClick={() => setStatusFilter(new Set())}
                    className="h-5 px-1.5 rounded border border-border text-muted-foreground hover:text-foreground"
                    data-testid="activities-status-kpi-clear">
              Clear 3D filter
            </button>
          )}
        </div>
      )}

      <div className="ag-theme-alpine flex-1">
        <AgGridReact
          ref={gridRef}
          rowData={filteredMappings}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          treeData={true}
          getDataPath={getDataPath}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDefaultExpanded={(followTimeline || slippingOnly || statusFilter.size > 0) ? 6 : 4}
          rowHeight={30}
          headerHeight={30}
          getRowStyle={heatStyle}
          onRowClicked={(e) => {
            if (e.data?._kind === 'MICRO') { onMicroRowClick?.(e.data); return; }
            // Opening the macro row shows the discipline activities behind it.
            if (e.node?.allChildrenCount) e.node.setExpanded(true);
            onRowClicked(e.data?.activityId);
          }}
          quickFilterText={gridSearchText}
          rowSelection="single"
          getRowId={p => p.data?.activityId}
          suppressCellFocus
          animateRows
        />
      </div>
    </div>
  );
}
